'use client';

import { useCreateUser } from '@/components/privy/useCreateUser';
import { useUser } from '@/hooks/useUser';
import { usePrivy } from '@privy-io/react-auth';
import { useStatsigClient } from '@statsig/react-bindings';
import { Suspense, useEffect } from 'react';
import { FaWallet } from 'react-icons/fa6';
import { ProfileDropdown } from '../profile/ProfileDropdown';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

function WalletButtonContent() {
  const { ready, authenticated, logout, user: privyUser, login } = usePrivy();
  const { checkGate } = useStatsigClient();
  const { user: appUser, isLoading: userLoading } = useUser();
  const { createUserIfNeeded } = useCreateUser();

  useEffect(() => {
    if (authenticated && privyUser && !userLoading && !appUser) {
      createUserIfNeeded().catch(console.error);
    }
  }, [authenticated, privyUser, appUser, userLoading, createUserIfNeeded]);

  if (!ready) {
    return (
      <div>
        <span className="block sm:hidden opacity-50">Wallet</span>
        <span className="sm:block hidden opacity-50">
          <FaWallet size={23} className="fill-gray-400" />
        </span>
      </div>
    );
  }

  const handleClick = async () => {
    if (!authenticated) {
      login();
    } else {
      // Optionally show profile dropdown or logout
      // logout();
    }
  };

  // Not authenticated: show sign in/connect wallet
  if (!authenticated) {
    return (
      <TooltipProvider delayDuration={250}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div onClick={handleClick} className="group cursor-pointer">
              <span className="block sm:hidden text-white group-hover:text-gray-400 transition-colors">
                Sign In / Connect Wallet
              </span>
              <span className="sm:block hidden">
                <FaWallet
                  size={23}
                  className="fill-white group-hover:fill-gray-400 transition-colors"
                />
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent>Sign In / Connect Wallet</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Authenticated and wallet: show profile dropdown if enabled
  if (authenticated && appUser) {
    const isProfileEnabled = checkGate('profile');
    if (isProfileEnabled) {
      return (
        <TooltipProvider delayDuration={250}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <ProfileDropdown />
              </span>
            </TooltipTrigger>
            <TooltipContent>User profile / settings</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
  }

  // Fallback: show logout
  return (
    <button
      onClick={logout}
      className="w-[30px] h-[30px] flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-800 transition-colors text-sm"
      style={{ minWidth: 30, minHeight: 30 }}
      aria-label="Log out"
    >
      Log out
    </button>
  );
}

export default function WalletButton() {
  return (
    <span className="inline-flex items-center justify-center w-[30px] h-[30px]">
      <Suspense fallback={<FaWallet size={23} className="fill-gray-400" />}>
        <WalletButtonContent />
      </Suspense>
    </span>
  );
}
